/**
 * Calibration Review — human-in-the-loop team clustering approval.
 *
 * THE DECISION THIS SCREEN MAKES IS PERMANENT. Approving dispatches the full
 * processing run with a cluster->team mapping baked in, and every analytic
 * downstream inherits it: get the swap wrong and the whole match is played by
 * the wrong two teams. So the naming logic is a tested pure module
 * (./calibration/teams) and this file is the screen around it.
 *
 * A port of the Jinja page that lived at /games/<job_id>/calibration/orig
 * (retired in Phase 1 of the port; the SPA now redirects that address
 * here). Same endpoints, same payloads, same derivation — the two
 * differences are deliberate: the timing form reports a bad clock in the page
 * flash instead of a window.alert(), and it posts to the address that
 * endpoint actually serves (the old page posted to /games/<id>/game-timing,
 * which no route has ever answered).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import MatchMarks, {
  BLANK_MARKS, MARKS, clockToSecs, marksProblem, secsToField, type MarkFields,
} from '../components/MatchMarks'
import { ApiError, api, type Session } from '../lib/api'
import CalibrationVideo from './calibration/CalibrationVideo'
import RenameMatch from './RenameMatch'
import type { VideoPlayerHandle } from './match/VideoPlayer'
import { dominantJerseyHex } from './calibration/jersey'
import { loadPlotly } from './calibration/plotly'
import {
  CLUSTER_COLORS, EXTRACTOR_LABELS, TUNABLE_VARIANTS, VARIANT_LABELS, VARIANT_ORDER,
  type Summary, type TeamContext,
  type BucketColor,
  activeEmbedding, activeVariants, buildApprovePayload, colorForBucket, countsImbalanced,
  deriveForVariant, displayNameFor, finalHomeOnTeamA, teamHint,
  uploadColorDisagrees,
  withRefitVariant,
} from './calibration/teams'
import './admin.css'
import './calibration-review.css'

type UploadMeta = {
  kickoff_time?: string
  halftime_time?: string
  second_half_time?: string
  final_whistle_time?: string
  home_team_start_direction?: string
  home_team_color?: string
}

type Context = {
  job_id: string
  game_id: number | null
  game_code: string
  runpod_status: string | null
  error_message: string | null
  status: string | null
  has_summary: boolean
  crop_urls: string[]
  team_context: TeamContext
  has_game_timing: boolean
  game_timing: Record<string, number>
  /** Why the marks can no longer change here (approved or run), or null. */
  marks_locked: string | null
  upload_metadata: UploadMeta
  has_video: boolean
  video_fps: number | null
}

type Flash = { kind: 'success' | 'error' | 'info'; text: string }

/** The upload form's field for each mark — shown beside it as a hint. */
const UPLOAD_KEY = {
  kickoff_s: 'kickoff_time', h1_end_s: 'halftime_time',
  h2_start_s: 'second_half_time', game_end_s: 'final_whistle_time',
} as const

const message = (e: unknown) => (e instanceof ApiError ? e.message : String(e))

export default function CalibrationReview({ session }: { session?: Session | null }) {
  const { jobId = '' } = useParams()
  const navigate = useNavigate()

  const [ctx, setCtx] = useState<Context | null>(null)
  const [ctxError, setCtxError] = useState<string | null>(null)
  const [summary, setSummary] = useState<Summary | null>(null)
  const [summaryFailed, setSummaryFailed] = useState(false)
  const [flash, setFlash] = useState<Flash | null>(null)

  const [variant, setVariant] = useState('kmeans')
  const [extractor, setExtractor] = useState<string | null>(null)
  const [userSwapped, setUserSwapped] = useState(false)
  const [tuned, setTuned] = useState<Record<string, number>>({})
  // Jersey colours belong to the PLAYERS in a crop bucket, not to its
  // label: each entry keeps the crops it was read from (detected) or set
  // beside (the reviewer's pick), and a bucket takes the colour of the
  // entry its crops overlap most (colorForBucket). Switching extractor or
  // re-fitting relabels the clusters; the colours follow the players.
  const [detected, setDetected] = useState<BucketColor[]>([])
  const [picked, setPicked] = useState<BucketColor[]>([])
  const colorRequested = useRef<Set<string>>(new Set())

  const [busy, setBusy] = useState<string | null>(null)
  // Teams & direction card: null means "tracking the server value".
  const [dirPick, setDirPick] = useState<'left' | 'right' | null>(null)
  const [timing, setTiming] = useState<MarkFields>(BLANK_MARKS)
  // Read-only mode for the teams card when the uploader set a direction:
  // the reviewer sees it and must explicitly opt in to editing.
  const [teamsEditing, setTeamsEditing] = useState(false)
  // The review video, for the timing card's Mark / Go / step buttons.
  const player = useRef<VideoPlayerHandle>(null)
  // The pop-out's <video> while that window is open (CalibrationVideo).
  const popout = useRef<HTMLVideoElement | null>(null)
  const getVideo = useCallback(() => {
    const p = popout.current
    if (p && p.ownerDocument.defaultView && !p.ownerDocument.defaultView.closed) return p
    return player.current?.getVideo() ?? null
  }, [])
  const [plotError, setPlotError] = useState<string | null>(null)
  const [refitStatus, setRefitStatus] = useState('')
  const [sliderPct, setSliderPct] = useState<number | null>(null)
  const scatterEl = useRef<HTMLDivElement | null>(null)
  const refitTimer = useRef<number | null>(null)

  const loadContext = useCallback(() => {
    api<Context>(`/api/v1/jobs/${jobId}/calibration/context`)
      .then((d) => { setCtx(d); setCtxError(null) })
      .catch((e) => setCtxError(message(e)))
  }, [jobId])

  useEffect(loadContext, [loadContext])

  // Prefill the timing form from what is stored, so the marks stay visible
  // and adjustable after a save (and across visits). Only fields the
  // reviewer has not typed into are filled — never clobber mid-edit text.
  useEffect(() => {
    const stored = ctx?.game_timing
    if (!stored) return
    setTiming((t) => {
      const next = { ...t }
      for (const m of MARKS) {
        if (next[m.key] === '' && stored[m.key] != null) {
          next[m.key] = secsToField(stored[m.key])
        }
      }
      return next
    })
  }, [ctx])

  // Success toasts dismiss themselves; errors — and the long-running
  // approve info — stay until replaced.
  useEffect(() => {
    if (flash?.kind !== 'success') return
    const t = window.setTimeout(() => setFlash(null), 4000)
    return () => window.clearTimeout(t)
  }, [flash])

  // The summary is the heavy artifact (embedding + variants), fetched only
  // once the context says there is one.
  useEffect(() => {
    if (!ctx?.has_summary) return
    let live = true
    api<Summary>(`/api/v1/jobs/${jobId}/calibration/summary`)
      .then((d) => {
        if (!live) return
        setSummary(d)
        const fxKeys = Object.keys(d.extractors ?? {})
        setExtractor(fxKeys.includes(d.feature_extractor ?? '')
          ? (d.feature_extractor as string)
          : (fxKeys[0] ?? d.feature_extractor ?? null))
      })
      .catch((e) => {
        if (!live) return
        setSummaryFailed(true)
        setFlash({ kind: 'error', text: `Failed to load calibration summary: ${message(e)}` })
      })
    return () => { live = false }
  }, [ctx?.has_summary, jobId])

  const variants = useMemo(() => activeVariants(summary, extractor), [summary, extractor])
  const available = useMemo(
    () => VARIANT_ORDER.filter((v) => variants[v]), [variants])

  // Grids can differ per extractor (dbscan finds no centroids in one
  // embedding), so the selected variant follows what this bundle computed.
  useEffect(() => {
    const first = available[0]
    if (first && !available.includes(variant)) setVariant(first)
  }, [available, variant])

  // Memoised, both of them: a fresh object each render would re-run the
  // derivation and re-draw the scatter on every keystroke in the timing form.
  const teamCtx = useMemo<TeamContext>(
    () => ctx?.team_context ?? { home: null, away: null, direction: null },
    [ctx?.team_context])
  // A direction picked in the Teams card but not yet saved still drives the
  // naming (and goes out with approve), so what the scatter shows is what
  // approval bakes in.
  const namingCtx = useMemo<TeamContext>(
    () => (dirPick ? { ...teamCtx, direction: dirPick } : teamCtx),
    [teamCtx, dirPick])
  const derived = useMemo(
    () => deriveForVariant(summary, extractor, variant, namingCtx),
    [summary, extractor, variant, namingCtx])
  const homeOnA = finalHomeOnTeamA(derived, userSwapped)
  const name = useCallback(
    (raw: string) => displayNameFor(raw, teamCtx, homeOnA), [teamCtx, homeOnA])

  const current = variants[variant]
  const counts = useMemo(() => current?.counts ?? {}, [current])

  // ── crops, bucketed by the CURRENT labels ────────────────────────────
  // Bundles carrying sample_crop_indices re-bucket as the slider, variant
  // and extractor change; legacy bundles keep their calibrate-time grouping.
  const cropBuckets = useMemo<[string, string[]][]>(() => {
    if (!summary) return []
    const idxs = summary.sample_crop_indices
    if (Array.isArray(idxs) && idxs.length && current?.labels) {
      const mapping = current.team_mapping ?? {}
      const buckets: Record<string, string[]> = {}
      idxs.forEach((i) => {
        const n = mapping[String(current.labels?.[i])] ?? 'other'
        ;(buckets[n] ??= []).push(`/api/v1/jobs/${jobId}/calibration/crops/s_${i}.jpg`)
      })
      const order = ['Team A', 'Team B']
      return Object.keys(buckets)
        .sort((a, b) => (order.indexOf(a) < 0 ? 99 : order.indexOf(a))
                      - (order.indexOf(b) < 0 ? 99 : order.indexOf(b)))
        .map((n): [string, string[]] => [n, buckets[n] ?? []])
    }
    // Legacy: crop_urls says which filenames exist, but the bucket's public
    // URLs do not resolve — they are served through the authenticated proxy.
    const known = new Set((ctx?.crop_urls ?? []).map((u) => u.split('/').pop()))
    const sample = summary.sample_crops ?? {}
    return Object.keys(sample).map((n) => [
      n,
      (sample[n] ?? []).filter((f) => known.has(f))
        .map((f) => `/api/v1/jobs/${jobId}/calibration/crops/${f}`),
    ])
  }, [summary, current, ctx?.crop_urls, jobId])

  // Each team bucket's colour: the reviewer's pick for those players, else
  // the colour detected from them; detected once per set of players.
  const teamColors = useMemo(() => {
    const out: Record<string, string> = {}
    cropBuckets.forEach(([raw, urls]) => {
      if (raw !== 'Team A' && raw !== 'Team B') return
      const hex = colorForBucket(urls, picked) ?? colorForBucket(urls, detected)
      if (hex) out[raw] = hex
    })
    return out
  }, [cropBuckets, picked, detected])
  const isPicked = useCallback(
    (urls: string[]) => colorForBucket(urls, picked) != null, [picked])
  useEffect(() => {
    cropBuckets.forEach(([raw, urls]) => {
      if (raw !== 'Team A' && raw !== 'Team B') return
      const key = [...urls].sort().join('|')
      if (!urls.length || colorRequested.current.has(key)) return
      if (colorForBucket(urls, detected) || colorForBucket(urls, picked)) return
      colorRequested.current.add(key)
      dominantJerseyHex(urls).then((hex) => {
        if (hex) setDetected((d) => [...d, { urls, hex }])
      })
    })
  }, [cropBuckets, detected, picked])

  // ── the 3D scatter ───────────────────────────────────────────────────
  useEffect(() => {
    const el = scatterEl.current
    if (!el || !current) return
    const labels = current.labels ?? []
    const mapping = current.team_mapping ?? {}
    const embedding = activeEmbedding(summary, extractor)
    const clusterIds = [...new Set(labels)].sort((a, b) => a - b)
    const traces = clusterIds.map((cid, idx) => {
      const xs: number[] = [], ys: number[] = [], zs: number[] = []
      for (let i = 0; i < embedding.length; i++) {
        if (labels[i] !== cid) continue
        const [x, y, z] = embedding[i] ?? []
        if (x == null || y == null || z == null) continue
        xs.push(x); ys.push(y); zs.push(z)
      }
      const raw = mapping[String(cid)] ?? `Cluster ${cid}`
      const count = counts[raw] ?? xs.length
      return {
        type: 'scatter3d', mode: 'markers',
        name: `${name(raw)} (${count})`,
        x: xs, y: ys, z: zs,
        marker: { size: 2.5, color: CLUSTER_COLORS[idx % CLUSTER_COLORS.length], opacity: 0.75 },
      }
    })
    let live = true
    loadPlotly()
      .then((Plotly) => {
        if (!live || !scatterEl.current) return
        setPlotError(null)
        return Plotly.react(scatterEl.current, traces, {
          margin: { l: 0, r: 0, t: 0, b: 0 },
          legend: { orientation: 'h', y: -0.05 },
          scene: {
            xaxis: { title: '', showticklabels: false },
            yaxis: { title: '', showticklabels: false },
            zaxis: { title: '', showticklabels: false },
          },
        }, { responsive: true })
      })
      .catch((e) => { if (live) setPlotError(message(e)) })
    return () => { live = false }
  }, [current, summary, extractor, counts, name])

  useEffect(() => () => {
    const el = scatterEl.current
    if (el && window.Plotly) window.Plotly.purge(el)
  }, [])

  // ── outlier percentile ───────────────────────────────────────────────
  const params = current?.params ?? {}
  const canRefit = TUNABLE_VARIANTS.includes(variant) && !!params.kmeans_cluster_centers
  const shownPct = sliderPct ?? tuned[variant] ?? params.outlier_percentile ?? 90

  // The slider is per embedding AND per variant: switching either retargets
  // it at that variant's own cut rather than carrying a number across.
  useEffect(() => { setSliderPct(null); setRefitStatus('') }, [variant, extractor])
  useEffect(() => { setTuned({}) }, [extractor])

  const onSlide = (value: number) => {
    setSliderPct(value)
    const frozen = variant   // the reviewer may switch mid-debounce
    if (refitTimer.current) window.clearTimeout(refitTimer.current)
    refitTimer.current = window.setTimeout(async () => {
      setRefitStatus('refitting…')
      try {
        const body: Record<string, unknown> = { outlier_percentile: value, variant: frozen }
        if (extractor) body.feature_extractor = extractor
        const r = await api<{
          labels: number[]; counts: Record<string, number>
          outlier_percentile: number; outlier_distance_threshold: number
        }>(`/api/v1/jobs/${jobId}/calibration/refit`,
           { method: 'POST', body: JSON.stringify(body) })
        setSummary((s) => (s ? withRefitVariant(s, extractor, frozen, r.labels, r.counts) : s))
        setTuned((t) => ({ ...t, [frozen]: r.outlier_percentile }))
        setRefitStatus(`threshold ${r.outlier_distance_threshold.toFixed(3)}`)
      } catch (e) {
        setRefitStatus(`refit failed: ${message(e)}`)
      }
    }, 250)
  }

  useEffect(() => () => { if (refitTimer.current) window.clearTimeout(refitTimer.current) }, [])

  // ── actions ──────────────────────────────────────────────────────────
  const forceReview = async () => {
    setBusy('force')
    try {
      await api(`/api/v1/jobs/${jobId}/calibration/force-review`, { method: 'POST' })
      loadContext()
    } catch (e) {
      setFlash({ kind: 'error', text: `Force failed: ${message(e)}` })
    } finally { setBusy(null) }
  }

  const saveTiming = async () => {
    const problem = marksProblem(timing)
    if (problem) {
      setFlash({ kind: 'error', text: problem })
      return
    }
    const values: Record<string, number> = {}
    for (const m of MARKS) {
      const v = clockToSecs(timing[m.key])
      if (v != null) values[m.key] = v
    }
    if (!Object.keys(values).length) {
      setFlash({ kind: 'error', text: 'Enter at least one timestamp.' })
      return
    }
    setBusy('timing')
    try {
      await api(`/api/v1/games/${jobId}/game-timing`,
                { method: 'POST', body: JSON.stringify({ game_timing: values }) })
      setFlash({ kind: 'success', text: 'Game timing saved.' })
      loadContext() // refresh the stored values and drop the nag styling
    } catch (e) {
      setFlash({ kind: 'error', text: `Could not save timing: ${message(e)}` })
    } finally { setBusy(null) }
  }

  // ── teams & direction ──────────────────────────────────────────────
  // Home is the game code's second team (AWAY@HOME) — what every reader
  // after this page uses, so it is shown, not chosen. The card records
  // which way home attacks in H1, which feeds the cluster naming above and
  // the approval payload.
  const shownHome = teamCtx.home
  const shownAway = teamCtx.away
  const shownDir = dirPick ?? teamCtx.direction

  const saveTeams = async () => {
    if (!shownDir) return
    setBusy('teams')
    try {
      await api(`/api/v1/games/${jobId}/team-context`, { method: 'POST',
        body: JSON.stringify({ home_team_start_direction: shownDir }) })
      setDirPick(null)
      loadContext() // re-derives naming, the hint and the scatter legends
      setFlash({ kind: 'success', text: 'Teams & direction saved.' })
    } catch (e) {
      setFlash({ kind: 'error', text: `Could not save teams: ${message(e)}` })
    } finally { setBusy(null) }
  }

  const approve = async () => {
    setBusy('approve')
    // Approve launches the RunPod synchronously and can block for two or
    // three minutes while RunPod finds capacity — say so rather than leaving
    // a dead disabled button.
    setFlash({ kind: 'info', text: 'Approving — launching the processing pod. '
      + 'This can take a couple of minutes, leave this tab open…' })
    try {
      const body = buildApprovePayload({
        variant, summary, extractor, tunedPercentile: tuned[variant],
        homeOnTeamA: homeOnA, teamColors, ctx: namingCtx,
      })
      await api(`/api/v1/jobs/${jobId}/calibration/approve`,
                { method: 'POST', body: JSON.stringify(body) })
      setFlash({ kind: 'success', text: `Calibration approved (${variant}). `
        + 'Processing started — taking you to the dashboard…' })
      window.setTimeout(() => navigate('/'), 1800)
    } catch (e) {
      setFlash({ kind: 'error', text: `Approve failed: ${message(e)}` })
      setBusy(null)
    }
  }

  const [recal, setRecal] = useState<Record<string, string>>({})
  const recalibrate = async () => {
    const body: Record<string, unknown> = {}
    const windows: Record<string, number[]> = {}
    if (recal.h1s !== '' && recal.h1s != null && recal.h1e !== '' && recal.h1e != null) {
      windows.half1 = [Number(recal.h1s), Number(recal.h1e)]
    }
    if (recal.h2s !== '' && recal.h2s != null && recal.h2e !== '' && recal.h2e != null) {
      windows.half2 = [Number(recal.h2s), Number(recal.h2e)]
    }
    if (Object.keys(windows).length) body.calibration_windows = windows
    if (recal.extractor) body.feature_extractor = recal.extractor
    setBusy('recal')
    try {
      await api(`/api/v1/jobs/${jobId}/calibration/recalibrate`,
                { method: 'POST', body: JSON.stringify(body) })
      setFlash({ kind: 'success',
                 text: 'Re-calibration started. Refresh this page in ~5 minutes for new results.' })
    } catch (e) {
      setFlash({ kind: 'error', text: `Re-calibrate failed: ${message(e)}` })
      setBusy(null)
    }
  }

  // ── render ───────────────────────────────────────────────────────────
  if (ctxError) {
    return <div className="panel cal-page"><p className="adm-note" role="alert">{ctxError}</p></div>
  }
  if (!ctx) return <div className="panel cal-page"><p className="muted">Loading…</p></div>

  const status = ctx.status ?? 'unknown'
  const um = ctx.upload_metadata ?? {}
  const locked = ctx.marks_locked
  const uploaderTiming = MARKS.filter((m) => um[UPLOAD_KEY[m.key]])
    .map((m) => `${m.label} ${um[UPLOAD_KEY[m.key]]}`)
  const hasUploadTeams = !!(um.home_team_start_direction)
  // The home jersey colour declared at upload, against what the crops show:
  // nearer the cluster named away means the naming or the colours are
  // swapped — said here, before approve stores them on the game.
  const homeRaw = homeOnA ? 'Team A' : 'Team B'
  const awayRaw = homeOnA ? 'Team B' : 'Team A'
  const uploadColorWarning = uploadColorDisagrees(
    um.home_team_color, teamColors[homeRaw], teamColors[awayRaw])

  return (
    <div className="cal-page">
      <div className="cal-header">
        <h2 className="cal-title">Calibration Review — {ctx.game_code}</h2>
        {/* A code typed wrong at upload is fixed here, before approve
            (Priori staff; the server checks it too). */}
        {(session?.user.is_staff || session?.user.is_platform_admin) && (
          <RenameMatch gameId={ctx.game_id} gameCode={ctx.game_code}
                       onRenamed={() => loadContext()} />
        )}
        <span className={`cal-pill cal-pill--${status}`}>{status}</span>
      </div>

      {flash && (
        <div className={`cal-flash cal-flash--${flash.kind}`}
             role={flash.kind === 'error' ? 'alert' : 'status'}>{flash.text}</div>
      )}

      {!ctx.has_summary ? (
        <div className="panel cal-card">
          <p className="cal-empty">
            No calibration results are available for this job yet.
            {ctx.status === 'recalibrating'
              && ' Re-calibration is running — check back in a few minutes.'}
          </p>
          {ctx.runpod_status === 'failed' && (
            <div className="cal-force">
              <p className="cal-force__why">
                Calibration failed the sample sanity gate
                {ctx.error_message ? ` — ${ctx.error_message}` : ''}.
              </p>
              <button type="button" className="adm-btn adm-btn--quiet"
                      disabled={busy === 'force'} onClick={forceReview}>
                {busy === 'force' ? 'Forcing…' : 'Force to review anyway'}
              </button>
              <span className="muted cal-force__note">
                Exposes the sparse calibration (however few images it captured)
                so you can cluster manually.
              </span>
            </div>
          )}
        </div>
      ) : (
        <>
          <div className="panel cal-card cal-quality">
            <div className="cal-stat">
              <div className="cal-stat__label">Samples</div>
              <div className="cal-stat__value">
                {summary?.n_samples != null ? summary.n_samples.toLocaleString() : '—'}
              </div>
            </div>
            <div className="cal-stat">
              <div className="cal-stat__label">Feature extractor</div>
              <div className="cal-stat__value">
                {extractor ?? summary?.feature_extractor ?? '—'}
              </div>
            </div>
            {Object.entries(counts).map(([team, count]) => (
              <div className="cal-stat" key={team}>
                <div className="cal-stat__label">{name(team)}</div>
                <div className="cal-stat__value">{count.toLocaleString()}</div>
              </div>
            ))}
            {countsImbalanced(counts) && (
              <span className="cal-badge-warn">Team counts imbalanced (&gt;30%)</span>
            )}
          </div>

          <div className="panel cal-card">
            <div className="cal-card__head">
              <h3 className="cal-h3">Team clustering (3D UMAP)</h3>
              <div className="cal-controls">
                <button type="button"
                        className={`adm-btn adm-btn--quiet${userSwapped ? ' is-swapped' : ''}`}
                        title="Flip which cluster gets the home team's name"
                        onClick={() => setUserSwapped((s) => !s)}>Swap teams</button>
                {Object.keys(summary?.extractors ?? {}).length > 1 && (
                  <div className="cal-toggle"
                       title="Feature extractor — both computed from the same crops">
                    {Object.keys(summary?.extractors ?? {}).map((fx) => (
                      <button type="button" key={fx}
                              className={fx === extractor ? 'is-active' : ''}
                              onClick={() => setExtractor(fx)}>
                        {EXTRACTOR_LABELS[fx] ?? fx}
                      </button>
                    ))}
                  </div>
                )}
                <div className="cal-toggle">
                  {available.map((v) => (
                    <button type="button" key={v}
                            className={v === variant ? 'is-active' : ''}
                            onClick={() => setVariant(v)}>
                      {VARIANT_LABELS[v] ?? v}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <p className="cal-hint">{teamHint(derived, namingCtx, userSwapped)}</p>

            {canRefit && (
              <div className="cal-slider">
                <label htmlFor="cal-pct">
                  Outlier percentile ({variant}):
                </label>
                <input id="cal-pct" type="range" min={50} max={100} step={0.1}
                       value={shownPct}
                       onChange={(e) => onSlide(Number(e.target.value))} />
                <input type="number" className="cal-pct-num" min={50} max={100}
                       step={0.1} value={shownPct}
                       aria-label="Outlier percentile (precise)"
                       onChange={(e) => onSlide(Number(e.target.value))} />
                <span className="muted">{refitStatus}</span>
              </div>
            )}

            <div className="cal-scatter" ref={scatterEl}>
              {!summary && !summaryFailed && <p className="cal-empty">Loading embedding…</p>}
              {(summaryFailed || plotError) && (
                <p className="cal-empty">{plotError ?? 'Could not load embedding.'}</p>
              )}
            </div>
          </div>

          <div className="panel cal-card">
            <h3 className="cal-h3">Sample crops per cluster</h3>
            {uploadColorWarning && (
              <p className="cal-color-warn" role="alert">
                The home jersey colour given at upload
                (<span className="cal-color__chip" style={{ background: um.home_team_color }} />
                {' '}{um.home_team_color}) looks more like {name(awayRaw)} than
                {' '}{name(homeRaw)}. Check the home/away naming and the jersey
                colours before approving.
              </p>
            )}
            {!summary && !summaryFailed && <p className="cal-empty">Loading crops…</p>}
            {summaryFailed && <p className="cal-empty">Could not load crops.</p>}
            {summary && !cropBuckets.length && (
              <p className="cal-empty">No sample crops available.</p>
            )}
            {cropBuckets.map(([raw, urls]) => (
              <div className="cal-crop-row" key={raw}>
                <div className="cal-crop-row__title">
                  {name(raw)}{summary?.sample_crop_indices ? ` — ${urls.length} shown` : ''}
                  {(raw === 'Team A' || raw === 'Team B') && (
                    <>
                      <input type="color" className="cal-color"
                             title="Jersey color stored with the game — adjust if the auto-detect is off"
                             value={teamColors[raw] ?? '#000000'}
                             onChange={(e) => {
                               const hex = e.target.value
                               setPicked((p) => [
                                 ...p.filter((x) => colorForBucket(urls, [x]) == null),
                                 { urls, hex }])
                             }} />
                      <span className="cal-color__note">
                        {isPicked(urls) ? 'jersey (set)'
                          : teamColors[raw] ? 'jersey' : 'detecting…'}
                      </span>
                    </>
                  )}
                </div>
                <div className="cal-crops">
                  {urls.map((u) => (
                    <img key={u} src={u} alt={`${raw} sample crop`} loading="lazy" />
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="panel cal-card">
            <div className="cal-card__head">
              <h3 className="cal-h3">Teams &amp; direction</h3>
              {hasUploadTeams && !teamsEditing && !locked && (
                <button type="button" className="adm-btn adm-btn--quiet"
                        onClick={() => setTeamsEditing(true)}>
                  Enable editing
                </button>
              )}
            </div>
            <p className="muted">
              Home is the second team of the game code ({ctx.game_code}). Which
              way home attacks in the first half drives the cluster naming
              above, and approval bakes that mapping in.
            </p>
            {locked && <p className="adm-note">{locked}</p>}
            <div className="cal-teams">
              <div>
                <label>Home team</label>
                <span className="cal-readonly">{shownHome ?? '—'}</span>
              </div>
              <div>
                <label>Away team</label>
                <span className="cal-readonly">{shownAway ?? '—'}</span>
              </div>
              {(hasUploadTeams && !teamsEditing) || locked ? (
                <div>
                  <label>Direction (1st half)</label>
                  <span className="cal-readonly">
                    {shownDir ? `${shownHome ?? 'Home'} attacks ${shownDir}` : '—'}
                  </span>
                  {um.home_team_start_direction && (
                    <span className="cal-origin">entered by uploader</span>
                  )}
                </div>
              ) : (
                <fieldset className="cal-teams__dir">
                  <legend>{shownHome ?? 'Home'} attacks (1st half)</legend>
                  {(['left', 'right'] as const).map((d) => (
                    <label key={d}>
                      <input type="radio" name="tc-dir" value={d}
                             checked={shownDir === d}
                             onChange={() => setDirPick(d)} />
                      {' '}{d}
                    </label>
                  ))}
                  {shownDir ? (
                    <span className="muted">
                      {`${shownAway ?? 'Away'} attacks ${shownDir === 'left' ? 'right' : 'left'}`}
                    </span>
                  ) : (
                    <span className="cal-teams__dir-missing">
                      not set, pick one before approving
                    </span>
                  )}
                </fieldset>
              )}
            </div>
            {!locked && (!hasUploadTeams || teamsEditing) && (
              <>
                {um.home_team_start_direction && (
                  <p className="cal-hint-orig">
                    uploader entered: {um.home_team_start_direction}
                  </p>
                )}
                <button type="button" className="adm-btn adm-btn--quiet"
                        disabled={busy === 'teams' || !shownDir} onClick={saveTeams}>
                  Save teams &amp; direction
                </button>
              </>
            )}
          </div>

          <div className="cal-actions">
            <div className="panel cal-card">
              <h3 className="cal-h3">Approve calibration</h3>
              <div className="cal-radios">
                {available.map((v) => (
                  <label key={v}>
                    <input type="radio" name="approve-variant" value={v}
                           checked={v === variant}
                           onChange={() => setVariant(v)} />
                    {' '}{VARIANT_LABELS[v] ?? v}
                  </label>
                ))}
              </div>
              <button type="button" className="adm-btn"
                      disabled={busy === 'approve'} onClick={approve}>
                {busy === 'approve' ? 'Launching processing pod…'
                  : 'Approve & start full processing'}
              </button>
            </div>

            <div className="panel cal-card">
              <h3 className="cal-h3">Re-calibrate</h3>
              <div className="cal-grid">
                <div>
                  <label htmlFor="r-h1s">Half 1 start frame</label>
                  <input id="r-h1s" type="number" min={0} placeholder="e.g. 0"
                         value={recal.h1s ?? ''}
                         onChange={(e) => setRecal((r) => ({ ...r, h1s: e.target.value }))} />
                </div>
                <div>
                  <label htmlFor="r-h1e">Half 1 end frame</label>
                  <input id="r-h1e" type="number" min={0} placeholder="e.g. 9000"
                         value={recal.h1e ?? ''}
                         onChange={(e) => setRecal((r) => ({ ...r, h1e: e.target.value }))} />
                </div>
                <div>
                  <label htmlFor="r-h2s">Half 2 start frame</label>
                  <input id="r-h2s" type="number" min={0} placeholder="optional"
                         value={recal.h2s ?? ''}
                         onChange={(e) => setRecal((r) => ({ ...r, h2s: e.target.value }))} />
                </div>
                <div>
                  <label htmlFor="r-h2e">Half 2 end frame</label>
                  <input id="r-h2e" type="number" min={0} placeholder="optional"
                         value={recal.h2e ?? ''}
                         onChange={(e) => setRecal((r) => ({ ...r, h2e: e.target.value }))} />
                </div>
              </div>
              <label htmlFor="r-fx">Feature extractor</label>
              <select id="r-fx" value={recal.extractor ?? ''}
                      onChange={(e) => setRecal((r) => ({ ...r, extractor: e.target.value }))}>
                <option value="">(keep current)</option>
                <option value="siglip">SigLIP</option>
                <option value="cv2">CV2 color</option>
                <option value="reid">ReID</option>
                <option value="siglip_reid">SigLIP + ReID</option>
              </select>
              <button type="button" className="adm-btn adm-btn--quiet"
                      disabled={busy === 'recal'} onClick={recalibrate}>Re-calibrate</button>
            </div>
          </div>
        </>
      )}

      {ctx.has_video && (
        <CalibrationVideo jobId={jobId} gameCode={ctx.game_code}
                          fps={ctx.video_fps} playerRef={player}
                          popoutRef={popout} />
      )}

      {/* Right under the match video, which its buttons drive (or the
          pop-out, while open). Optional by design — approval never blocks
          on it — but these marks drive dead-ball filtering, GK half
          assignment and video sync. */}
      <div className={`panel cal-card${ctx.has_game_timing ? '' : ' cal-card--nag'}`}>
        <div className="cal-card__head">
          <h3 className="cal-h3">
            {ctx.has_game_timing ? 'Game timing' : 'No game timing set'}
          </h3>
        </div>
        <p className="muted">
          Kickoff / halftime / final whistle drive dead-ball filtering, GK
          team assignment and video sync. Play or step the match video
          below to each moment and press Mark — these are what the
          processing run is given.
        </p>
        {uploaderTiming.length > 0 && (
          <p className="cal-hint-orig">uploader entered: {uploaderTiming.join(' · ')}</p>
        )}
        {locked && <p className="adm-note">{locked}</p>}
        <MatchMarks getVideo={getVideo} fps={ctx.video_fps} fields={timing}
                    disabled={!!locked}
                    onChange={(k, v) => setTiming((t) => ({ ...t, [k]: v }))} />
        {!locked && (
          <button type="button" className="adm-btn adm-btn--quiet"
                  disabled={busy === 'timing'} onClick={saveTiming}>
            Save game timing
          </button>
        )}
      </div>

    </div>
  )
}
